/**
 * What «Pegar lista» (contract v10.52) holds while the person goes through the answer of
 * the tariffs: for each line, whether it goes in, which article and how many. Out of the
 * component so the rules are tested without a screen, and because a component file that
 * exports functions breaks fast refresh.
 */
import { quantityFor, type TariffLineAdd } from '@/shared/quotes/basket';
import { editableNumber } from '@/shared/utils/money';

import type { ListLine } from './types';

export interface ListChoice {
  include: boolean;
  /** The article chosen: the only one of a `found` line, none yet for a `choose` one. */
  itemId: string | null;
  /** As typed, Spanish: the API gets it through `quantityFor`. */
  quantity: string;
}

type Hit = NonNullable<ListLine['options']>[number];

/**
 * Whether this person may add the article (contract v10.52): one without a list price is
 * priced from what Cubas pays, so only whoever sees the purchase may. A back before the
 * flag answers nothing, and then it may.
 */
export function quotable(hit: Hit): boolean {
  return hit.quotable !== false;
}

/**
 * Where the person starts: every article found goes in with the quantity the list said;
 * a line with several suppliers (or one read out of a sentence) goes in too, but waits for
 * one to be chosen. Nothing is chosen for the person between two suppliers: one is
 * cheaper, the other is the one the customer asked for, and only the person knows which
 * matters today. An article this person may not add starts out.
 */
export function initialChoices(lines: readonly ListLine[]): ListChoice[] {
  return lines.map((line) => {
    const quantity = editableNumber(line.quantity ?? null) || '1';
    const options = (line.options ?? []).filter(quotable);
    if (line.status === 'found' && options[0]) {
      return { include: true, itemId: options[0].id, quantity };
    }
    if (line.status === 'choose' && options.length > 0) {
      return { include: true, itemId: null, quantity };
    }
    return { include: false, itemId: null, quantity: '' };
  });
}

/**
 * «Usar este proveedor para el resto de la lista»: every line still waiting for a choice
 * that this supplier has takes its article. A choice already made is not touched. Answers
 * how many lines it chose, for the line that says so.
 */
export function chooseSupplierForRest(
  lines: readonly ListLine[],
  choices: readonly ListChoice[],
  supplierId: string,
): { choices: ListChoice[]; chosen: number } {
  let chosen = 0;
  const next = choices.map((choice, index) => {
    const line = lines[index];
    if (line?.status !== 'choose' || choice.itemId !== null) return choice;
    const option = line.options?.find((hit) => hit.supplier_id === supplierId && quotable(hit));
    if (!option) return choice;
    chosen += 1;
    return { ...choice, itemId: option.id };
  });
  return { choices: next, chosen };
}

/** How many other lines «Usar este proveedor para el resto» would choose. */
export function restWithSupplier(
  lines: readonly ListLine[],
  choices: readonly ListChoice[],
  supplierId: string,
  except: number,
): number {
  return lines.filter(
    (line, index) =>
      index !== except &&
      line.status === 'choose' &&
      choices[index]?.itemId === null &&
      (line.options ?? []).some((hit) => hit.supplier_id === supplierId && quotable(hit)),
  ).length;
}

/** What goes into the draft, in the order of the list. */
export function itemsToAdd(choices: readonly ListChoice[]): TariffLineAdd[] {
  const items: TariffLineAdd[] = [];
  for (const choice of choices) {
    const quantity = quantityFor(choice.quantity);
    if (choice.include && choice.itemId && quantity) {
      items.push({ item_id: choice.itemId, quantity });
    }
  }
  return items;
}

/** Lines ticked that will not go in: no supplier chosen, or a quantity that is not one. */
export function leftOut(
  lines: readonly ListLine[],
  choices: readonly ListChoice[],
): {
  unchosen: number;
  badQuantity: number;
} {
  let unchosen = 0;
  let badQuantity = 0;
  choices.forEach((choice, index) => {
    if (!choice.include) return;
    if (lines[index]?.status === 'choose' && choice.itemId === null) unchosen += 1;
    else if (!quantityFor(choice.quantity)) badQuantity += 1;
  });
  return { unchosen, badQuantity };
}
